// src/pages/evcc/EvccMemberDiscounts.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Button,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";

const pageBg = "#f9fbfe";
const sectionHeaderBg = "#ccd87cff";
const sectionHeaderBorder = "#9fad3d";
const linkBlue = "#7e895d";

function SectionCard({ title, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #dde4f0",
        mb: 4,
      }}
    >
      <Box
        sx={{
          backgroundColor: sectionHeaderBg,
          borderTop: `4px solid ${sectionHeaderBorder}`,
          px: 3,
          py: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1d2736" }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>{children}</Box>
    </Paper>
  );
}

function BulletList({ items }) {
  return (
    <List dense sx={{ pl: 0, mt: 0.5 }}>
      {items.map((text) => (
        <ListItem key={text} sx={{ pl: 0 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <ArrowRightIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={text}
            primaryTypographyProps={{ variant: "body2", color: "#111827" }}
          />
        </ListItem>
      ))}
    </List>
  );
}

export default function EvccMemberDiscounts() {
  return (
    <Box sx={{ backgroundColor: pageBg, minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
          Home &gt; EVCC &gt; EVCC Member Discounts
        </Typography>

        {/* Title + intro */}
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1, color: "#111827" }}
        >
          EVCC Member Discounts
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: "#4b5563", maxWidth: 900 }}>
          As an EVCC member, you can access a range of exclusive discounts and
          partner offers that support your professional development, protect your
          business, and help you stand out with consumers.
        </Typography>

        {/* Institution of Engineering and Technology */}
        <SectionCard title="Institution of Engineering and Technology (IET)">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            sx={{ mb: 2 }}
          >
            {/* Logo placeholder – replace with actual <img> when ready */}
            <Box
              sx={{
                width: 160,
                height: 80,
                borderRadius: 2,
                backgroundColor: "#eef2ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: "#4b5563",
              }}
            >
              IET logo
            </Box>

            <Typography variant="body2" sx={{ color: "#4b5563" }}>
              The Institution of Engineering and Technology (IET) provides specialist
              guidance and training for EV charging professionals.
            </Typography>
          </Stack>

          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
            EVCC member offer
          </Typography>
          <BulletList
            items={[
              "25% discount on the IET’s Code of Practice for EV Charging Equipment Installation.",
              "25% discount on the IET Online Academy Course: Electric Vehicle Charging Equipment Installation to BS7671: 2018 AMD 1.",
            ]}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
            Key reasons to join
          </Typography>
          <BulletList
            items={[
              "Access specialist training and resources at a reduced cost to support your professional development.",
              "On completing the Academy Course, you can apply to Trade Skills 4U for assessment to achieve the City & Guilds 2919 qualification – a fast-track path to industry-recognised accreditation.",
            ]}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
            How to access
          </Typography>
          <Typography variant="body2" sx={{ color: "#4b5563" }}>
            Just provide evidence of your EVCC membership number to IET when purchasing
            the Code of Practice or enrolling on the Academy course.
          </Typography>

          {/* Optional external link button (set real URL later) */}
          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            size="small"
            component="a"
            href="#"
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit IET website
          </Button>
        </SectionCard>

        {/* Which? Trusted Traders */}
        <SectionCard title="Which? Trusted Traders">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            sx={{ mb: 2 }}
          >
            <Box
              sx={{
                width: 160,
                height: 80,
                borderRadius: 2,
                backgroundColor: "#eef2ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: "#4b5563",
              }}
            >
              Which? Trusted Traders logo
            </Box>

            <Typography variant="body2" sx={{ color: "#4b5563" }}>
              Run by Which?, the UK’s most trusted consumer champion. Their endorsement
              logo helps businesses stand out in competitive markets.
            </Typography>
          </Stack>

          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
            EVCC member offer
          </Typography>
          <BulletList
            items={[
              "50% discount on the Which? Trusted Trader membership fee for your first 6 months.",
            ]}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
            Key reasons to join
          </Typography>
          <BulletList
            items={[
              "Boost your credibility and win more customers with a respected endorsement at half the cost.",
            ]}
          />

          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            size="small"
            component="a"
            href="https://trustedtraders.which.co.uk/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit Which? Trusted Traders
          </Button>
        </SectionCard>

        {/* Marsh Commercial */}
        <SectionCard title="Marsh Commercial">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            sx={{ mb: 2 }}
          >
            <Box
              sx={{
                width: 160,
                height: 80,
                borderRadius: 2,
                backgroundColor: "#eef2ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: "#4b5563",
              }}
            >
              Marsh Commercial logo
            </Box>

            <Typography variant="body2" sx={{ color: "#4b5563" }}>
              Marsh Commercial is a leading insurance broker with expertise in supporting
              businesses across the UK. They understand the risks faced in the renewables
              sector and, in partnership with EVCC, offer specialist insurance with
              tailored coverage at competitive rates.
            </Typography>
          </Stack>

          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
            EVCC member benefit
          </Typography>
          <BulletList
            items={[
              "Access business insurance through Marsh Commercial with dedicated support and guidance for EVCC members.",
              "Secure tailored insurance solutions that protect your business at member-only rates.",
            ]}
          />

          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            size="small"
            component="a"
            href="#"
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact Marsh Commercial
          </Button>
        </SectionCard>

        {/* installHUB */}
        <SectionCard title="Install Hub">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            sx={{ mb: 2 }}
          >
            <Box
              sx={{
                width: 160,
                height: 80,
                borderRadius: 2,
                backgroundColor: "#eef2ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: "#4b5563",
              }}
            >
              installHUB logo
            </Box>

            <Typography variant="body2" sx={{ color: "#4b5563" }}>
              installHUB is a centralised control hub for field operatives, enabling
              head office–to–field connectivity for efficient, professional and
              compliant installation services.
            </Typography>
          </Stack>

          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
            EVCC member offer
          </Typography>
          <BulletList
            items={[
              "Discount on installHUB software to support your EV installation operations.",
            ]}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
            Key reasons to join
          </Typography>
          <BulletList
            items={[
              "Streamline operations and improve service delivery with discounted industry software.",
            ]}
          />

          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            size="small"
            component="a"
            href="#"
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit installHUB
          </Button>
        </SectionCard>
      </Container>
    </Box>
  );
}
